import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import EthiopianFlag from '../components/EthiopianFlag';
import './LandingV3.css';

/**
 * Landing V3: Parent-Focused
 *
 * Theme: "Your Child's Future, Unlocked"
 * Tone: Warm, aspirational, trustworthy
 * Target: Parents of Grade 11-12 students
 *
 * Key Messages:
 * - Education determines future opportunities
 * - Quality tutoring was only for the privileged
 * - Lomi AI makes it accessible to all
 * - Trust, safety, proven results
 */

const LandingV3: React.FC = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      quote: "My daughter was struggling in Biology. After two months with Lomi AI, she went from failing to one of the top students in her class. I couldn't afford a private tutor, but Lomi gave her the same quality help.",
      name: "Almaz T.",
      role: "Mother of Grade 12 student",
      location: "Bahir Dar"
    },
    {
      quote: "As a single father working two jobs, I couldn't help my son with his studies. Lomi AI became the tutor I couldn't be. He studies every evening now, and I can see his confidence growing.",
      name: "Tadesse K.",
      role: "Father of Grade 11 student",
      location: "Addis Ababa"
    },
    {
      quote: "We live in a small town with no good tutors. Lomi AI gave my children access to the same quality education as students in Addis. That's priceless.",
      name: "Hiwot M.",
      role: "Mother of two students",
      location: "Jimma"
    }
  ];

  const comparisons = [
    {
      feature: 'Availability',
      traditional: 'Limited hours, scheduling conflicts',
      lomi: 'Available 24/7, whenever your child needs help'
    },
    {
      feature: 'Cost',
      traditional: '500-2000 ETB/month per subject',
      lomi: 'All subjects at a fraction of the cost'
    },
    {
      feature: 'Personalization',
      traditional: 'Same pace for everyone',
      lomi: 'Adapts to your child\'s specific weaknesses'
    },
    {
      feature: 'Real exam prep',
      traditional: 'Depends on tutor quality',
      lomi: 'Trained on actual ESSLCE questions'
    },
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="landing-v3">
      {/* Navigation */}
      <nav className="nav-v3">
        <div className="nav-brand-v3">
          <Logo height={40} />
        </div>
        <div className="nav-links-v3">
          <button className="nav-link-v3" onClick={() => navigate('/about')}>
            About
          </button>
          <button className="nav-link-v3 nav-cta-v3" onClick={() => navigate('/login')}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-v3">
        <div className="hero-content-v3">
          <div className="hero-badge-v3">
            <span className="badge-flag"><EthiopianFlag size={14} /></span>
            Trusted by Ethiopian families
          </div>

          <h1 className="hero-title-v3">
            Every Ethiopian Child Deserves a<br />
            <span className="title-highlight">World-Class Education</span>
          </h1>

          <p className="hero-subtitle-v3">
            <strong>Lomi AI</strong> is the personal tutor your child deserves —
            available 24/7, affordable, and designed specifically for the Ethiopian curriculum.
          </p>

          <div className="hero-cta-v3">
            <button
              className="cta-primary-v3"
              onClick={() => navigate('/login')}
            >
              Give Your Child an Advantage
            </button>
            <button
              className="cta-secondary-v3"
              onClick={() => navigate('/about')}
            >
              Learn Our Story
            </button>
          </div>

          <div className="trust-signals-v3">
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Free to try</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>Safe & secure</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">✓</span>
              <span>No commitments</span>
            </div>
          </div>
        </div>

        <div className="hero-image-v3">
          <div className="image-frame">
            <div className="student-success-image">
              <div className="success-emoji">🎓</div>
              <div className="success-text">
                <span className="success-headline">Your child's success</span>
                <span className="success-subline">starts here</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="problem-v3">
        <div className="problem-content-v3">
          <h2>The Reality Ethiopian Families Face</h2>

          <div className="problem-stats-v3">
            <div className="problem-stat urgent">
              <div className="stat-value">Most</div>
              <div className="stat-desc">students do not pass</div>
            </div>
            <div className="problem-stat">
              <div className="stat-value">15x</div>
              <div className="stat-desc">gap between best and worst schools</div>
            </div>
          </div>

          <p className="problem-explanation">
            The difference between success and failure isn't intelligence.
            <strong> It's access to quality teaching.</strong>
          </p>

          <p className="problem-empathy">
            Until now, quality tutoring was a privilege only the wealthy could afford.
            Private tutors cost thousands of birr per month — out of reach for most families.
          </p>
        </div>
      </section>

      {/* The Solution */}
      <section className="solution-v3">
        <div className="solution-content-v3">
          <div className="solution-intro">
            <span className="solution-icon">🍋</span>
            <h2>Introducing Lomi AI</h2>
            <p className="solution-tagline">
              A personal AI tutor that gives every Ethiopian child the same quality
              education as the best private schools — at a price every family can afford.
            </p>
          </div>

          <div className="solution-features-v3">
            <div className="solution-feature">
              <div className="feature-visual">📱</div>
              <h3>Always Available</h3>
              <p>
                Your child can study anytime — early morning, late night, weekends.
                Lomi AI never gets tired and never judges. It's patient,
                encouraging, and always ready to explain again.
              </p>
            </div>

            <div className="solution-feature">
              <div className="feature-visual">🎯</div>
              <h3>Personalized Learning</h3>
              <p>
                Lomi AI identifies exactly where your child struggles and focuses there.
                No wasted time on concepts they already know. Every minute of study
                time is maximized.
              </p>
            </div>

            <div className="solution-feature">
              <div className="feature-visual">📝</div>
              <h3>Real ESSLCE Preparation</h3>
              <p>
                Lomi AI is trained on actual past ESSLCE exams. Your child practices
                with real questions and learns the exact patterns that appear on the exam.
                No surprises on exam day.
              </p>
            </div>

            <div className="solution-feature">
              <div className="feature-visual"><EthiopianFlag size={48} /></div>
              <h3>Made for Ethiopia</h3>
              <p>
                Built by Ethiopians, for Ethiopians. Lomi AI understands the Ethiopian
                curriculum, uses examples your child relates to, and teaches in a way
                that makes sense for our context.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="comparison-v3">
        <div className="comparison-content-v3">
          <h2>How Lomi AI Compares</h2>

          <div className="comparison-table-v3">
            <div className="comparison-header">
              <div className="comparison-col"></div>
              <div className="comparison-col traditional">Traditional Tutoring</div>
              <div className="comparison-col lomi">Lomi AI</div>
            </div>

            {comparisons.map((item, i) => (
              <div className="comparison-row" key={i}>
                <div className="comparison-col feature">{item.feature}</div>
                <div className="comparison-col traditional">{item.traditional}</div>
                <div className="comparison-col lomi">{item.lomi}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-v3">
        <div className="testimonials-content-v3">
          <h2>What Parents Are Saying</h2>

          <div className="testimonial-carousel-v3">
            <div className="testimonial-card-v3">
              <div className="quote-icon">"</div>
              <p className="testimonial-quote">
                {testimonials[activeTestimonial].quote}
              </p>
              <div className="testimonial-author-v3">
                <div className="author-avatar-v3">👤</div>
                <div className="author-details">
                  <strong>{testimonials[activeTestimonial].name}</strong>
                  <span>{testimonials[activeTestimonial].role}</span>
                  <span className="author-location">📍 {testimonials[activeTestimonial].location}</span>
                </div>
              </div>
            </div>

            <div className="testimonial-dots">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={`dot ${activeTestimonial === i ? 'active' : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Trust */}
      <section className="trust-v3">
        <div className="trust-content-v3">
          <h2>Your Child's Safety is Our Priority</h2>

          <div className="trust-grid-v3">
            <div className="trust-card-v3">
              <div className="trust-card-icon">🔒</div>
              <h3>Safe & Secure</h3>
              <p>All data is encrypted and protected. We never share your child's information.</p>
            </div>
            <div className="trust-card-v3">
              <div className="trust-card-icon">👁️</div>
              <h3>Parent Visibility</h3>
              <p>Track your child's progress anytime. See what they're learning and where they're improving.</p>
            </div>
            <div className="trust-card-v3">
              <div className="trust-card-icon">✅</div>
              <h3>Age-Appropriate</h3>
              <p>Content is designed specifically for Grade 11-12 students. Nothing inappropriate.</p>
            </div>
            <div className="trust-card-v3">
              <div className="trust-card-icon"><EthiopianFlag size={32} /></div>
              <h3>Ethiopian-Built</h3>
              <p>Created by an Ethiopian team who understands our values and educational needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Start */}
      <section className="start-v3">
        <div className="start-content-v3">
          <h2>Getting Started is Easy</h2>

          <div className="start-steps-v3">
            <div className="start-step">
              <div className="step-number-v3">1</div>
              <h3>Sign Up Free</h3>
              <p>One tap with Telegram or Gmail. No forms, no setup.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="start-step">
              <div className="step-number-v3">2</div>
              <h3>Set Up Your Child</h3>
              <p>Enter their grade and subjects. Lomi AI personalizes immediately.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="start-step">
              <div className="step-number-v3">3</div>
              <h3>Start Learning</h3>
              <p>Your child begins with their first lesson. Watch them grow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-v3">
        <div className="final-cta-content-v3">
          <h2>Your Child's Future Starts Today</h2>
          <p>
            Don't let your child sit that exam without support.
            Give them the advantage they deserve.
          </p>
          <button
            className="cta-primary-v3 large"
            onClick={() => navigate('/login')}
          >
            Start Your Child's Journey
          </button>
          <p className="cta-note">One tap — sign in with Telegram or Gmail</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-v3">
        <div className="footer-content-v3">
          <div className="footer-brand-v3">
            <Logo variant="dark" height={36} />
          </div>
          <p className="footer-mission-v3">
            Making quality education accessible to every Ethiopian child.
          </p>

          <div className="footer-links-v3">
            <button onClick={() => navigate('/about')}>About</button>
            <button onClick={() => navigate('/contact')}>Contact</button>
            <button onClick={() => navigate('/privacy')}>Privacy</button>
            <button onClick={() => navigate('/terms')}>Terms</button>
          </div>

          <div className="footer-company-v3">
            <p>Built with ❤️ in Ethiopia</p>
            <p className="copyright-v3">© {new Date().getFullYear()} Lomi. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingV3;
